import { BaseEffect } from '../BaseEffect.js';

/**
 * Lens distortion effect implementation
 * Creates realistic lens distortion with barrel/pincushion, chromatic fringing, and vignette
 */
export class LensDistortionEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            distortion: 0.0,      // Barrel/pincushion distortion (-1 to 1)
            cubicDistortion: 0.0, // Higher order distortion (-1 to 1)
            fringing: 0.2,        // Chromatic fringing at edges
            vignette: 0.3,        // Vignette darkness
            scale: 1.0,          // Scale to compensate for distortion
            centerX: 0.0,        // Horizontal lens center offset (-1 to 1)
            centerY: 0.0,        // Vertical lens center offset (-1 to 1)
            animate: true,      // Whether to animate the distortion
            pulseSpeed: 1.0,     // Speed of the pulsing animation
            pulseAmount: 0.2     // Amount of parameter pulsing
        };

        super('lens-distortion', shaderManager, defaultParams);
    }

    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            precision highp float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform float u_distortion;
            uniform float u_cubicDistortion;
            uniform float u_fringing;
            uniform float u_vignette;
            uniform float u_scale;
            uniform float u_centerX;
            uniform float u_centerY;
            
            varying vec2 v_texcoord;

            vec2 distort(vec2 uv, float chromatic) {
                // Center the coordinates with offset
                vec2 center = vec2(0.5 + u_centerX * 0.5, 0.5 + u_centerY * 0.5);
                vec2 coords = (uv - center);
                
                // Apply aspect ratio correction
                float aspect = u_resolution.x / u_resolution.y;
                coords.x *= aspect;
                
                // Calculate radius and distort
                float r2 = dot(coords, coords);
                float r4 = r2 * r2;
                
                // Combine quadratic and cubic distortion
                float distortion = u_distortion * (1.0 + chromatic * u_fringing);
                float cubic = u_cubicDistortion * (1.0 + chromatic * u_fringing * 0.5);
                
                // Calculate distortion factor
                float factor = 1.0 + distortion * r2 + cubic * r4;
                
                // Scale the distorted coordinates
                coords *= factor * u_scale;
                
                // Unapply aspect ratio correction
                coords.x /= aspect;
                
                // Return to original coordinate space
                return coords + center;
            }

            void main() {
                vec2 uv = v_texcoord;
                
                // Sample with chromatic aberration
                vec2 redOffset = distort(uv, 1.0);
                vec2 greenOffset = distort(uv, 0.0);
                vec2 blueOffset = distort(uv, -1.0);
                
                // Sample each color channel
                float r = texture2D(u_image, redOffset).r;
                float g = texture2D(u_image, greenOffset).g;
                float b = texture2D(u_image, blueOffset).b;
                float a = texture2D(u_image, greenOffset).a;
                
                // Combine channels
                vec4 color = vec4(r, g, b, a);
                
                // Calculate vignette
                vec2 vignetteCenter = vec2(0.5 + u_centerX * 0.5, 0.5 + u_centerY * 0.5);
                vec2 vignetteCoords = uv - vignetteCenter;
                vignetteCoords.x *= u_resolution.x / u_resolution.y;
                float vignetteFactor = 1.0 - dot(vignetteCoords, vignetteCoords) * u_vignette * 2.0;
                vignetteFactor = pow(clamp(vignetteFactor, 0.0, 1.0), 0.5);
                
                // Apply vignette
                color.rgb *= vignetteFactor;
                
                gl_FragColor = color;
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    setupUniforms() {
        super.setupUniforms();
        
        this.addUniform('u_distortion', { type: '1f' });
        this.addUniform('u_cubicDistortion', { type: '1f' });
        this.addUniform('u_fringing', { type: '1f' });
        this.addUniform('u_vignette', { type: '1f' });
        this.addUniform('u_scale', { type: '1f' });
        this.addUniform('u_centerX', { type: '1f' });
        this.addUniform('u_centerY', { type: '1f' });
    }

    updateUniforms(uniforms) {
        super.updateUniforms(uniforms);
        
        // Get base values
        let distortion = this.params.distortion;
        let cubicDistortion = this.params.cubicDistortion;
        let fringing = this.params.fringing;
        let vignette = this.params.vignette;
        let scale = this.params.scale;
        let centerX = this.params.centerX;
        let centerY = this.params.centerY;
        
        // Add pulsing animation to multiple parameters if enabled
        if (this.params.animate && uniforms.u_time) {
            const time = uniforms.u_time.value * this.params.pulseSpeed;
            const amount = this.params.pulseAmount;
            
            // Create different frequencies for organic motion
            const pulses = {
                slow: Math.sin(time * 0.3),
                medium: Math.sin(time * 0.7),
                fast: Math.sin(time * 1.1),
                veryFast: Math.sin(time * 1.7)
            };
            
            // Modulate each parameter with different frequencies
            distortion += pulses.medium * amount * 0.3;
            cubicDistortion += pulses.slow * amount * 0.2;
            fringing += pulses.fast * amount * 0.15;
            vignette += pulses.medium * amount * 0.1;
            scale += pulses.slow * amount * 0.05;
            
            // Create orbital motion for center point
            centerX += Math.cos(time * 0.5) * amount * 0.2;
            centerY += Math.sin(time * 0.7) * amount * 0.2;
        }
        
        Object.assign(uniforms, {
            u_distortion: { value: distortion, type: '1f' },
            u_cubicDistortion: { value: cubicDistortion, type: '1f' },
            u_fringing: { value: fringing, type: '1f' },
            u_vignette: { value: vignette, type: '1f' },
            u_scale: { value: scale, type: '1f' },
            u_centerX: { value: centerX, type: '1f' },
            u_centerY: { value: centerY, type: '1f' }
        });

        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    getUIConfig() {
        return {
            distortion: {
                min: -1,
                max: 1,
                step: 0.01,
                label: 'Barrel/Pincushion'
            },
            cubicDistortion: {
                min: -1,
                max: 1,
                step: 0.01,
                label: 'Higher Order'
            },
            fringing: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Color Fringing'
            },
            vignette: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Vignette'
            },
            scale: {
                min: 0.5,
                max: 1.5,
                step: 0.01,
                label: 'Scale'
            },
            centerX: {
                min: -1,
                max: 1,
                step: 0.01,
                label: 'Center X'
            },
            centerY: {
                min: -1,
                max: 1,
                step: 0.01,
                label: 'Center Y'
            },
            animate: {
                label: 'Animate'
            },
            pulseSpeed: {
                min: 0.1,
                max: 3.0,
                step: 0.1,
                label: 'Pulse Speed'
            },
            pulseAmount: {
                min: 0,
                max: 0.5,
                step: 0.01,
                label: 'Pulse Amount'
            }
        };
    }
} 